import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MapGL, { type MapRef } from "react-map-gl/mapbox";

import type { MapEdge, MapNode } from "@thalamos/aspire-common";

import { config } from "../../../config.js";
import { useIsMobile } from "../hooks/useIsMobile.js";
import { D3Overlay } from "./D3Overlay.js";
import { NeuronFlashManager } from "./NeuronFlashManager.js";
import { NodeTooltip } from "./NodeTooltip.js";

const MAPBOX_TOKEN = config.mapboxAccessToken ?? "";

/** UK bounding box */
const UK_BOUNDS: [[number, number], [number, number]] = [
  [-8.6, 49.9],
  [1.8, 60.9],
];

interface NetworkMapProps {
  nodes: MapNode[];
  edges: MapEdge[];
  loading: boolean;
  focusBounds?: [[number, number], [number, number]] | null;
  initialMapPosition?: { lat: number; lng: number; zoom: number } | null;
  onMapMove?: (lat: number, lng: number, zoom: number) => void;
}

export function NetworkMap({
  nodes,
  edges,
  loading,
  focusBounds,
  initialMapPosition,
  onMapMove,
}: NetworkMapProps) {
  const mapRef = useRef<MapRef>(null);
  const [hoveredNode, setHoveredNode] = useState<MapNode | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(
    null,
  );
  const isMobile = useIsMobile();

  const nodeMap = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  // Fit to UK on initial load (skip if URL already has a position)
  const hasNodes = nodes.length > 0;
  useEffect(() => {
    const map = mapRef.current;
    if (map && hasNodes && !initialMapPosition) {
      map.fitBounds(UK_BOUNDS, { padding: 40, duration: 0 });
    }
  }, [hasNodes, initialMapPosition]);

  // Zoom to trust bounds or reset to UK when focus changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (focusBounds) {
      map.fitBounds(focusBounds, { padding: 80, duration: 2000, maxZoom: 11 });
    } else {
      map.fitBounds(UK_BOUNDS, { padding: 40, duration: 1500 });
    }
  }, [focusBounds]);

  // Sync map position → URL on moveend
  useEffect(() => {
    const m = mapRef.current?.getMap();
    if (!m || !onMapMove) return;
    const handler = () => {
      const centre = m.getCenter();
      onMapMove(centre.lat, centre.lng, m.getZoom());
    };
    m.on("moveend", handler);
    return () => {
      m.off("moveend", handler);
    };
  }, [onMapMove]);

  const handleNodeHover = useCallback(
    (node: MapNode | null, screenPos?: { x: number; y: number }) => {
      setHoveredNode(node);
      setTooltipPos(screenPos ?? null);
    },
    [],
  );

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <MapGL
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{
          longitude: initialMapPosition?.lng ?? -2.5,
          latitude: initialMapPosition?.lat ?? 54.5,
          zoom: initialMapPosition?.zoom ?? 5.5,
        }}
        style={{ width: "100%", height: "100%" }}
        mapStyle="mapbox://styles/mapbox/dark-v11"
        // react-map-gl otherwise loads Mapbox's right-to-left text plugin from
        // api.mapbox.com, which our script-src CSP does not allow. The map
        // covers England, so unshaped RTL labels cost nothing.
        RTLTextPlugin={false}
        attributionControl={false}
        dragRotate={false}
        touchPitch={false}
        pitchWithRotate={false}
        touchZoomRotate={true}
      >
        <D3Overlay
          nodes={nodes}
          edges={edges}
          mapRef={mapRef}
          onNodeHover={handleNodeHover}
        />
        <NeuronFlashManager edges={edges} nodeMap={nodeMap} mapRef={mapRef} />
      </MapGL>

      {loading && (
        <div
          style={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(0,0,0,0.8)",
            color: "#94a3b8",
            padding: "8px 16px",
            borderRadius: 8,
            fontSize: 14,
            zIndex: 50,
          }}
        >
          Loading…
        </div>
      )}

      {/* Zoom +/− buttons */}
      <div
        style={{
          position: "absolute",
          bottom: 24,
          right: 24,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          zIndex: 50,
        }}
      >
        {[
          { label: "+", delta: 1 },
          { label: "−", delta: -1 },
        ].map(({ label, delta }) => (
          <button
            key={label}
            onClick={() => {
              const map = mapRef.current?.getMap();
              if (!map) return;
              map.zoomTo(map.getZoom() + delta, { duration: 300 });
            }}
            style={{
              width: 36,
              height: 36,
              borderRadius: 6,
              border: "1px solid rgba(148,163,184,0.3)",
              background: "rgba(15,23,42,0.85)",
              color: "#e2e8f0",
              fontSize: 20,
              lineHeight: 1,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(8px)",
            }}
            aria-label={delta > 0 ? "Zoom in" : "Zoom out"}
          >
            {label}
          </button>
        ))}
      </div>

      {hoveredNode && tooltipPos && (
        <NodeTooltip
          node={hoveredNode}
          x={tooltipPos.x}
          y={tooltipPos.y}
          edges={edges}
          nodeMap={nodeMap}
          mode={isMobile ? "topbar" : "floating"}
          onDismiss={
            isMobile
              ? () => {
                  setHoveredNode(null);
                  setTooltipPos(null);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}
